import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../ThemeContext';

export default function AccountMenu({
  authUser,
  isPro,
  onSignOut,
  onDeleteAccount,
  onReset,
  onManageSubscription,
  syncStatus,
}) {
  const { t } = useTheme();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const initials = (() => {
    if (!authUser) return '?';
    if (authUser.displayName) return authUser.displayName.charAt(0).toUpperCase();
    if (authUser.email) return authUser.email.charAt(0).toUpperCase();
    return '?';
  })();

  const handleDeleteAccount = async () => {
    setOpen(false);
    const confirmed = window.confirm(
      'Are you sure you want to permanently delete your account and all data? This cannot be undone.'
    );
    if (confirmed) {
      await onDeleteAccount();
    }
  };

  const syncDot = (() => {
    if (syncStatus === 'syncing') return { color: '#0061a4', spinning: true };
    if (syncStatus === 'synced')  return { color: '#1b6d24', spinning: false };
    if (syncStatus === 'error')   return { color: '#f44336', spinning: false };
    return { color: '#bdbdbd', spinning: false };
  })();

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          position: 'relative',
          width: '34px',
          height: '34px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #0061a4, #004880)',
          color: 'white',
          fontWeight: '700',
          fontSize: '14px',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'inherit',
          flexShrink: 0,
        }}
        aria-label="Account menu"
        aria-expanded={open}
      >
        {initials}
        <span style={{
          position: 'absolute',
          bottom: '1px',
          right: '1px',
          width: '9px',
          height: '9px',
          borderRadius: '50%',
          background: syncDot.color,
          border: `2px solid ${t.card}`,
          animation: syncDot.spinning ? 'syncPulse 1s ease-in-out infinite' : 'none',
        }} />
      </button>

      <style>{`
        @keyframes syncPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          background: t.card,
          borderRadius: '10px',
          boxShadow: t.shadowLarge,
          border: `1px solid ${t.border}`,
          minWidth: '190px',
          zIndex: 200,
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '12px 16px',
            fontSize: '12px',
            color: t.textMuted,
            borderBottom: `1px solid ${t.borderLight}`,
            lineHeight: '1.4',
            wordBreak: 'break-all',
          }}>
            {authUser?.email || 'Signed in'}
          </div>

          <div style={{ padding: '4px 0' }}>
            {isPro && (
              <MenuItem
                label="Manage Subscription"
                onClick={() => { setOpen(false); onManageSubscription(); }}
              />
            )}
            <MenuItem
              label="Reset & Start Over"
              onClick={() => { setOpen(false); onReset(); }}
              color="#ba1a1a"
            />
          </div>

          <div style={{ borderTop: `1px solid ${t.borderLight}`, padding: '4px 0' }}>
            <MenuItem
              label="Delete Account"
              onClick={handleDeleteAccount}
              color="#f44336"
            />
            <MenuItem
              label="Sign Out"
              onClick={() => { setOpen(false); onSignOut(); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({ label, onClick, color }) {
  const { t } = useTheme();
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'block',
        width: '100%',
        padding: '9px 16px',
        background: hovered ? t.pageBg : 'transparent',
        border: 'none',
        textAlign: 'left',
        fontSize: '13px',
        color: color || t.textSub,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background 0.1s',
      }}
    >
      {label}
    </button>
  );
}
